import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import bannersModule, { BannerTypes } from '@gin-betting/banners';
import { BannerZoneSettings } from '@gin-betting/constants';
import { difference, find, forEach, map, reduce } from 'lodash';

import { useMemoSelector } from 'hooks';
import { BannersTuples, makePropsSelector } from './selectors';

const {
    actions: { getBannersData },
    selectors: { makeSelectMissingBannerZoneIds },
} = bannersModule;

type ZoneIntervals = { [key: string]: number };

interface Input {
    zoneLocation: BannerTypes.BannerZoneLocation;
}

interface Output {
    zoneIntervals: ZoneIntervals;
    banners: BannersTuples;
}

const useBannersCarousel = ({ zoneLocation }: Input): Output => {
    const dispatch = useDispatch();
    const requestedZonesCacheRef = useRef<string[]>([]);
    const location = useLocation();

    const { zones, banners } = useMemoSelector(
        makePropsSelector(zoneLocation),
        { location }
    );

    const zoneIds = useMemo(() => map(zones, zone => zone.id), [zones]);

    const missingZoneIds: string[] = useMemoSelector(
        makeSelectMissingBannerZoneIds(zoneIds),
        {},
        [zoneIds]
    );

    const uniqIds = difference(missingZoneIds, requestedZonesCacheRef.current);

    useEffect(() => {
        forEach(uniqIds, zoneId => {
            requestedZonesCacheRef.current.push(zoneId);

            dispatch(
                getBannersData({
                    zoneId,
                    withSportEvent: false,
                })
            );
        });
    }, [zones, dispatch, uniqIds]);

    const zoneIntervals: ZoneIntervals = useMemo(
        () =>
            reduce(
                zones,
                (acc: ZoneIntervals, zone) => {
                    const interval = find(
                        zone.settings,
                        ({ name }) => name === BannerZoneSettings.INTERVAL
                    );

                    if (interval) {
                        acc[zone.id] = +interval.value;
                    }

                    return acc;
                },
                {}
            ),
        [zones]
    );

    return {
        zoneIntervals,
        banners,
    };
};

export default useBannersCarousel;
